/**
 * React Hooks for Supabase Reports
 * Provides easy-to-use hooks for fetching and managing report data
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { 
  reportService, 
  SupabaseReport, 
  ReportFilters, 
  PaginationOptions, 
  ReportResponse 
} from '@/integrations/supabase/reportService';

/**
 * Hook to fetch reports with filtering and pagination
 */
export function useSupabaseReports(
  filters: ReportFilters = {},
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useQuery({
    queryKey: ['supabase-reports', filters, pagination],
    queryFn: () => reportService.getReports(filters, pagination),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch a single report by ID
 */
export function useSupabaseReport(
  id: string,
  enabled: boolean = true
): UseQueryResult<SupabaseReport | null, Error> {
  return useQuery({
    queryKey: ['supabase-report', id],
    queryFn: () => reportService.getReportById(id),
    enabled: enabled && !!id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch a single report by legacy ID (SQL Server ID)
 */
export function useSupabaseReportByLegacyId(
  legacyId: string,
  enabled: boolean = true
): UseQueryResult<SupabaseReport | null, Error> {
  return useQuery({
    queryKey: ['supabase-report-legacy', legacyId],
    queryFn: () => reportService.getReportByLegacyId(legacyId),
    enabled: enabled && !!legacyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook to fetch distinct periods
 */
export function useReportPeriods(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['report-periods'],
    queryFn: () => reportService.getPeriods(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch distinct teams
 */
export function useReportTeams(): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: ['report-teams'],
    queryFn: () => reportService.getTeams(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook to fetch reports statistics
 */
export function useReportsStats(): UseQueryResult<{
  totalReports: number;
  reportsWithAI: number;
  uniquePeriods: number;
  uniqueTeams: number;
  latestSyncDate: string | null;
}, Error> {
  return useQuery({
    queryKey: ['reports-stats'],
    queryFn: () => reportService.getReportsStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to search reports
 */
export function useSearchReports(
  searchTerm: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useQuery({
    queryKey: ['search-reports', searchTerm, pagination],
    queryFn: () => reportService.searchReports(searchTerm, pagination),
    enabled: enabled && searchTerm.trim().length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch recent reports (last 30 days)
 */
export function useRecentReports(
  limit: number = 20,
  enabled: boolean = true
): UseQueryResult<SupabaseReport[], Error> {
  return useQuery({
    queryKey: ['recent-reports', limit],
    queryFn: () => reportService.getRecentReports(limit),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to fetch reports by period
 */
export function useReportsByPeriod(
  period: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useSupabaseReports(
    { period },
    pagination,
    enabled && !!period
  );
}

/**
 * Hook to fetch reports by team
 */
export function useReportsByTeam(
  teamId: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useSupabaseReports(
    { id_team: teamId },
    pagination,
    enabled && !!teamId
  );
}

/**
 * Hook to fetch reports by user (creator)
 */
export function useReportsByUser(
  userId: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useSupabaseReports(
    { created_by_user_id: userId },
    pagination,
    enabled && !!userId
  );
}

/**
 * Hook to fetch reports by date range
 */
export function useReportsByDateRange(
  dateFrom: string,
  dateTo: string,
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useSupabaseReports(
    { date_from: dateFrom, date_to: dateTo },
    pagination,
    enabled && !!dateFrom && !!dateTo
  );
}

/**
 * Hook to fetch reports by multiple periods
 */
export function useReportsByPeriods(
  periods: string[],
  pagination: PaginationOptions = {},
  enabled: boolean = true
): UseQueryResult<ReportResponse, Error> {
  return useSupabaseReports(
    { periods },
    pagination,
    enabled && periods.length > 0
  );
}

/**
 * Default export with commonly used hooks
 */
export default {
  useSupabaseReports,
  useSupabaseReport,
  useSupabaseReportByLegacyId,
  useReportPeriods,
  useReportTeams,
  useReportsStats,
  useSearchReports,
  useRecentReports,
  useReportsByPeriod,
  useReportsByTeam,
  useReportsByUser,
  useReportsByDateRange,
  useReportsByPeriods,
};